/**
 * @fileoverview Utility to apply model presets to project agents.
 *
 * This module provides the logic to resolve a preset's layered configuration
 * (base + overrides) into concrete model settings for each agent.
 */

import type { ProjectAgent } from '@/domains/agents/schemas';
import type { ModelPreset, PresetId, PresetModelConfig } from './schema';
import { MODEL_PRESETS } from './definitions';

/**
 * The preset used to derive default model configurations for agents.
 * This is the single source of truth for what model an agent uses
 * when no project-specific or caller override is provided.
 */
export const DEFAULT_PRESET_ID: PresetId = 'default';

/**
 * Resolve the default model config for an agent from the default preset.
 *
 * This replaces the old per-agent `defaultConfig` field. Instead of each
 * agent definition declaring its own default model, the default preset
 * (base config + agent overrides) is the single source of truth.
 *
 * @param agentId - The agent ID to get default config for
 * @returns The resolved model configuration (provider + model name)
 */
export function resolveDefaultModelConfig(agentId: string): PresetModelConfig {
  return resolveAgentConfig(MODEL_PRESETS[DEFAULT_PRESET_ID], agentId);
}

/**
 * Resolve the model configuration for a specific agent within a preset.
 *
 * Uses the layered approach:
 * 1. Check if the agent has a specific override in the preset
 * 2. Fall back to the preset's base config
 *
 * @param preset - The preset to resolve from
 * @param agentId - The agent ID to get config for
 * @returns The resolved model configuration
 */
export function resolveAgentConfig(
  preset: ModelPreset,
  agentId: string
): PresetModelConfig {
  // Check for agent-specific override first
  const override = preset.agentOverrides[agentId];
  if (override) {
    return override;
  }

  // Fall back to base config
  return preset.baseConfig;
}

/**
 * Apply a preset to an array of project agents.
 *
 * This function takes the current agents and returns a new array with
 * updated modelProvider and modelName fields based on the preset.
 * All other agent properties (name, description, prompt, etc.) are preserved.
 *
 * @param presetId - The ID of the preset to apply
 * @param agents - The current project agents
 * @returns New array of agents with updated model configurations
 */
export function applyPresetToAgents(
  presetId: PresetId,
  agents: ProjectAgent[]
): ProjectAgent[] {
  return applyPresetObjectToAgents(MODEL_PRESETS[presetId], agents);
}

/**
 * Apply a fully-resolved preset object to an array of project agents.
 *
 * Use this when you already hold the effective preset (e.g. merged with
 * admin overrides) and want to avoid a second lookup against the code-
 * defined `MODEL_PRESETS` map.
 */
export function applyPresetObjectToAgents(
  preset: ModelPreset,
  agents: ProjectAgent[]
): ProjectAgent[] {
  return agents.map((agent) => {
    const config = resolveAgentConfig(preset, agent.id);
    return {
      ...agent,
      modelProvider: config.modelProvider,
      modelName: config.modelName,
    };
  });
}

/**
 * Get a summary of what changes a preset would make.
 *
 * Useful for showing a preview before applying.
 *
 * @param presetId - The preset to summarize
 * @param agents - The current agents to compare against
 * @returns Summary of changes
 */
export function getPresetChangeSummary(
  presetId: PresetId,
  agents: ProjectAgent[]
): {
  totalAgents: number;
  agentsToChange: number;
  willUseBase: number;
  willUseOverride: number;
} {
  return getPresetObjectChangeSummary(MODEL_PRESETS[presetId], agents);
}

export function getPresetObjectChangeSummary(
  preset: ModelPreset,
  agents: ProjectAgent[]
): {
  totalAgents: number;
  agentsToChange: number;
  willUseBase: number;
  willUseOverride: number;
} {
  let agentsToChange = 0;
  let willUseBase = 0;
  let willUseOverride = 0;

  for (const agent of agents) {
    const newConfig = resolveAgentConfig(preset, agent.id);
    const hasOverride = !!preset.agentOverrides[agent.id];

    if (hasOverride) {
      willUseOverride++;
    } else {
      willUseBase++;
    }

    // Check if this would actually change the agent's config
    if (
      agent.modelProvider !== newConfig.modelProvider ||
      agent.modelName !== newConfig.modelName
    ) {
      agentsToChange++;
    }
  }

  return {
    totalAgents: agents.length,
    agentsToChange,
    willUseBase,
    willUseOverride,
  };
}
